import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CurrentUser, HeaderColor, HeaderStyle, Standard } from '@td/shared';
import { del, get, patch, postForm } from '../lib/api';

export function useStandards(grade: string | null, subject?: string) {
  return useQuery({
    queryKey: ['standards', grade, subject ?? 'all'],
    queryFn: () => {
      const params = new URLSearchParams();
      if (grade) params.set('grade', grade);
      if (subject) params.set('subject', subject);
      return get<Standard[]>(`/standards?${params}`);
    },
    enabled: !!grade,
    // Reference data — it only changes when the state publishes new standards.
    staleTime: 60 * 60 * 1000,
  });
}

export function useStandardGrades() {
  return useQuery({
    queryKey: ['standard-grades'],
    queryFn: () => get<string[]>('/standards/grades'),
    staleTime: 60 * 60 * 1000,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      gradeLevel?: string | null;
      schoolYearStart?: string | null;
      schoolYearEnd?: string | null;
      bodyFont?: string | null;
      headingFont?: string | null;
      theme?: string;
      background?: string | null;
      backgroundTexture?: string | null;
      textureStrength?: number | null;
      timeZone?: string | null;
      avatarMark?: string | null;
      textScale?: number | null;
      headingScale?: number | null;
      schoolYearPending?: boolean;
      emailBackups?: boolean;
      headerStyle?: HeaderStyle | null;
      headerColor?: HeaderColor | null;
      navOrder?: string[] | null;
      navHidden?: string[] | null;
    }) => patch<CurrentUser>('/auth/settings', input),
    onSuccess: (user) => qc.setQueryData(['auth', 'me'], user),
  });
}

/**
 * A photograph of the teacher, for beside her name.
 *
 * Sent as multipart and re-encoded by the server, like every other image
 * here: what ends up stored is bytes sharp produced rather than bytes a
 * browser handed over, and the EXIF — which on a phone photo carries where it
 * was taken — is dropped on the way through.
 */
export function useUploadPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('image', file);
      return postForm<CurrentUser>('/auth/photo', body);
    },
    onSuccess: (user) => qc.setQueryData(['auth', 'me'], user),
  });
}

export function useDeletePhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => del<CurrentUser>('/auth/photo'),
    onSuccess: (user) => qc.setQueryData(['auth', 'me'], user),
  });
}
